import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calculator, CheckCircle2, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORES = [
  "Industrial y manufactura", "Distribución y comercio mayorista", "Construcción",
  "Ingeniería y servicios técnicos", "Servicios profesionales", "Tecnología y software",
  "Salud y servicios sanitarios", "Transporte y logística", "Alimentación y bebidas",
  "Hostelería y restauración", "Comercio minorista", "Medioambiente y gestión de residuos",
  "Alquiler de maquinaria y equipos", "Educación y formación", "Otros",
];

const YEARS = ["2023", "2024", "2025"];
const CONC = [{ v: "<20", l: "<20 %" }, { v: "20-40", l: "20-40 %" }, { v: "40-60", l: "40-60 %" }, { v: ">60", l: ">60 %" }];
const REC = [{ v: "<20", l: "<20 %" }, { v: "20-50", l: "20-50 %" }, { v: ">50", l: ">50 %" }];
const ANT = [{ v: "<5", l: "<5 años" }, { v: "5-15", l: "5-15 años" }, { v: ">15", l: ">15 años" }];
const INV = [{ v: "baja", l: "Baja" }, { v: "media", l: "Media" }, { v: "alta", l: "Alta" }];
const SINO = [{ v: "si", l: "Sí" }, { v: "no", l: "No" }];
const HORIZONTE = [
  { v: "<1", l: "Menos de 1 año" }, { v: "1-3", l: "1-3 años" },
  { v: ">3", l: "Más de 3 años" }, { v: "curiosidad", l: "Solo curiosidad" },
];

const LEGAL_TXT = "Estimación orientativa basada en múltiplos de mercado e información facilitada por el usuario, no verificada. No constituye una valoración profesional, un informe pericial ni una oferta de compra.";

const parseNum = (s) => Number(String(s).replace(/[.\s]/g, "").replace(",", "."));
const fmt = (n) => new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(n);

const inputCls =
  "w-full border border-gold/20 bg-white/[0.04] px-4 py-3.5 text-sm text-white outline-none transition-all placeholder:text-slate-500 focus:border-gold focus:ring-1 focus:ring-gold/40";

const Field = ({ label, hint, children }) => (
  <div>
    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">{label}</label>
    {children}
    {hint ? <p className="mt-2 text-xs leading-relaxed text-slate-500">{hint}</p> : null}
  </div>
);

const ChoiceGroup = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2.5" data-testid={testid}>
    {options.map((o) => (
      <button
        key={o.v}
        type="button"
        data-testid={`${testid}-${o.v}`}
        onClick={() => onChange(o.v)}
        className={`border px-4 py-2.5 text-sm font-medium transition-all duration-300 ${
          value === o.v
            ? "border-gold bg-gold text-ink"
            : "border-gold/25 bg-white/[0.03] text-slate-300 hover:border-gold/60 hover:text-gold"
        }`}
      >
        {o.l}
      </button>
    ))}
  </div>
);

export default function Valuation() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pub, setPub] = useState(null);
  const [score, setScore] = useState(null);
  const [done, setDone] = useState(false);

  const [fin, setFin] = useState({
    sector: SECTORES[0], facturacion_2023: "", facturacion_2024: "", facturacion_2025: "",
    ebitda_2023: "", ebitda_2024: "", ebitda_2025: "", empleados: "",
  });
  const [cual, setCual] = useState({
    concentracion_clientes: "", dependencia_propietario: "", ingresos_recurrentes: "",
    equipo_directivo: "", antiguedad_empresa: "", inmuebles_balance: "", inversion_mantenimiento: "",
  });
  const [lead, setLead] = useState({
    nombre: "", empresa: "", cargo: "", email: "", telefono: "",
    horizonte_venta: "", deuda: "", tesoreria: "", consent_privacidad: false, consent_comercial: false,
  });

  const setFinField = (k) => (e) => setFin({ ...fin, [k]: e.target.value });
  const setLeadField = (k) => (e) => setLead({ ...lead, [k]: e.target.value });

  const finPayload = () => ({
    sector: fin.sector,
    facturacion_2023: parseNum(fin.facturacion_2023),
    facturacion_2024: parseNum(fin.facturacion_2024),
    facturacion_2025: parseNum(fin.facturacion_2025),
    ebitda_2023: parseNum(fin.ebitda_2023),
    ebitda_2024: parseNum(fin.ebitda_2024),
    ebitda_2025: parseNum(fin.ebitda_2025),
    empleados: parseInt(fin.empleados, 10),
  });

  const submitStep1 = async (e) => {
    e.preventDefault();
    const p = finPayload();
    if ([p.facturacion_2023, p.facturacion_2024, p.facturacion_2025].some((v) => !(v > 0)) ||
        [p.ebitda_2023, p.ebitda_2024, p.ebitda_2025].some((v) => Number.isNaN(v)) ||
        !(p.empleados >= 0)) {
      toast.error("Revisa los datos: la facturación debe ser positiva y todos los campos son obligatorios.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/valuation/public`, p);
      setPub(data);
    } catch (err) {
      if (err.response?.status === 429) toast.error("Demasiadas peticiones. Inténtalo más tarde.");
      else toast.error(err.response?.data?.detail || "No se pudo calcular. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    if (Object.values(cual).some((v) => !v)) {
      toast.error("Responde a todas las preguntas para continuar.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/valuation/score`, { financiero: finPayload(), cualitativo: cual });
      if (!data.aplicable) {
        setPub({ aplicable: false, mensaje: data.mensaje });
        setStep(1);
        return;
      }
      setScore(data);
    } catch (err) {
      if (err.response?.status === 429) toast.error("Demasiadas peticiones. Inténtalo más tarde.");
      else toast.error("No se pudo calcular el índice. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const submitStep3 = async (e) => {
    e.preventDefault();
    if (!lead.consent_privacidad) {
      toast.error("Debes aceptar la política de privacidad.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/valuation/lead`, {
        financiero: finPayload(),
        cualitativo: cual,
        nombre: lead.nombre, empresa: lead.empresa, cargo: lead.cargo, email: lead.email,
        telefono: lead.telefono, horizonte_venta: lead.horizonte_venta,
        deuda: lead.deuda ? parseNum(lead.deuda) : null,
        tesoreria: lead.tesoreria ? parseNum(lead.tesoreria) : null,
        consent_privacidad: lead.consent_privacidad, consent_comercial: lead.consent_comercial,
      });
      setDone(true);
    } catch (err) {
      if (err.response?.status === 429) toast.error("Demasiadas peticiones. Inténtalo más tarde.");
      else toast.error("No se pudo enviar. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const panel = () => {
    if (pub && !pub.aplicable) {
      return (
        <div data-testid="valuation-notaplicable">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Resultado</p>
          <p className="mt-5 font-serif text-2xl leading-snug text-white">{pub.mensaje}.</p>
          <button
            onClick={() => scrollToId("#contacto")}
            data-testid="valuation-contact-cta"
            className="mt-8 flex items-center gap-3 border border-gold/60 px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
          >
            Hablemos de tu caso <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      );
    }
    if (step === 3 && score) {
      return (
        <div data-testid="valuation-step3-panel">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Último paso</p>
          <p className="mt-5 font-serif text-2xl leading-snug text-white">
            Tu informe detallado con el valor ajustado llegará a tu email.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-slate-300">
            Índice de calidad de tu empresa: <strong className="text-gold">{score.indice} / 100</strong>
          </p>
        </div>
      );
    }
    if (score && step === 2) {
      return (
        <div data-testid="valuation-score-result">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Índice de calidad de la empresa</p>
          <p className="mt-5 font-serif text-6xl text-white">
            {score.indice}<span className="text-2xl italic text-gold"> / 100</span>
          </p>
          {score.factores_negativos.length > 0 && (
            <div className="mt-7">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold/80">
                Lo que más resta valor ahora mismo
              </p>
              <ul className="mt-4 space-y-3">
                {score.factores_negativos.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm text-slate-300">
                    <TrendingDown className="h-4 w-4 shrink-0 text-gold" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <button
            onClick={() => setStep(3)}
            data-testid="valuation-to-step3"
            className="mt-8 flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-ink transition-all duration-300 hover:bg-gold-light"
          >
            Continuar al informe <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      );
    }
    if (pub && pub.aplicable) {
      return (
        <div data-testid="valuation-public-result">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">
            Horquilla orientativa de valor de empresa
          </p>
          <p className="mt-5 font-serif text-4xl leading-tight text-white sm:text-5xl">
            {fmt(pub.ev_min)} € <span className="italic text-gold">–</span> {fmt(pub.ev_max)} €
          </p>
          <p className="mt-5 text-sm leading-relaxed text-slate-300">{pub.nota}</p>
          {step === 1 && (
            <button
              onClick={() => setStep(2)}
              data-testid="valuation-to-step2"
              className="mt-8 flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-ink transition-all duration-300 hover:bg-gold-light"
            >
              Afinar mi valoración <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="flex flex-col items-start gap-5" data-testid="valuation-empty">
        <span className="flex h-12 w-12 items-center justify-center border border-gold/40">
          <Calculator className="h-6 w-6 text-gold" />
        </span>
        <p className="text-sm leading-relaxed text-slate-400">
          Completa los datos financieros de tu empresa y obtén al instante una horquilla orientativa de su valor.
        </p>
      </div>
    );
  };

  return (
    <section id="valoracion" data-testid="valuation-section" className="relative py-24 md:py-36">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(800px 420px at 8% 20%, rgba(192,160,98,0.07), transparent 60%)" }}
      />
      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        <Reveal>
          <p className="eyebrow">Calculadora de valoración</p>
          <h2 className="mt-6 font-serif text-4xl leading-tight text-white sm:text-5xl">¿Cuánto vale tu empresa?</h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">
            Tres pasos: una horquilla orientativa al instante, un índice de calidad de tu empresa y un informe
            detallado que recibirás por email.
          </p>
        </Reveal>

        {done ? (
          <Reveal className="mt-14">
            <div
              data-testid="valuation-done"
              className="mx-auto max-w-2xl border border-gold/25 bg-ink-3/60 p-10 text-center md:p-14"
            >
              <CheckCircle2 className="mx-auto h-12 w-12 text-gold" />
              <p className="mt-6 font-serif text-3xl leading-snug text-white">
                Hemos enviado el informe a tu email.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-slate-300">
                Un socio de AJG te contactará en 24-48 h para comentar los resultados de forma confidencial.
              </p>
              <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
                <a
                  href="https://wa.me/34654943319?text=Hola%2C%20acabo%20de%20solicitar%20mi%20informe%20de%20valoraci%C3%B3n."
                  target="_blank"
                  rel="noreferrer"
                  data-testid="valuation-done-whatsapp"
                  className="flex items-center justify-center gap-3 bg-[#25D366] px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-white transition-all duration-300 hover:brightness-110"
                >
                  WhatsApp
                </a>
                <a
                  href="tel:+34654943319"
                  data-testid="valuation-done-call"
                  className="flex items-center justify-center gap-3 border border-gold/60 px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
                >
                  Agendar llamada
                </a>
              </div>
            </div>
          </Reveal>
        ) : (
          <div className="mt-12 grid gap-12 lg:grid-cols-2 lg:items-start">
            <Reveal>
              <div className="mb-8">
                <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-gold" data-testid="valuation-step-label">
                  Paso {step} de 3
                </p>
                <div className="mt-3 flex gap-2">
                  {[1, 2, 3].map((s) => (
                    <span key={s} className={`h-1 flex-1 transition-colors duration-500 ${s <= step ? "bg-gold" : "bg-gold/20"}`} />
                  ))}
                </div>
              </div>

              <AnimatePresence mode="wait">
                {step === 1 && (
                  <motion.form
                    key="s1"
                    initial={{ opacity: 0, x: -18 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 18 }}
                    transition={{ duration: 0.35 }}
                    onSubmit={submitStep1}
                    data-testid="valuation-step1-form"
                    className="space-y-6"
                  >
                    <Field label="Sector">
                      <select
                        data-testid="valuation-sector"
                        value={fin.sector}
                        onChange={setFinField("sector")}
                        className={`${inputCls} appearance-none bg-ink-2`}
                      >
                        {SECTORES.map((s) => (
                          <option key={s} value={s} className="bg-ink-2 text-white">{s}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Facturación de los últimos 3 ejercicios (€)">
                      <div className="grid grid-cols-3 gap-3">
                        {YEARS.map((y) => (
                          <input
                            key={y}
                            data-testid={`valuation-fact-${y}`}
                            value={fin[`facturacion_${y}`]}
                            onChange={setFinField(`facturacion_${y}`)}
                            inputMode="numeric"
                            placeholder={y}
                            className={inputCls}
                          />
                        ))}
                      </div>
                    </Field>
                    <Field label="EBITDA de los últimos 3 ejercicios (€)" hint="Beneficio operativo antes de intereses, impuestos, depreciaciones y amortizaciones. Puede ser negativo.">
                      <div className="grid grid-cols-3 gap-3">
                        {YEARS.map((y) => (
                          <input
                            key={y}
                            data-testid={`valuation-ebitda-${y}`}
                            value={fin[`ebitda_${y}`]}
                            onChange={setFinField(`ebitda_${y}`)}
                            inputMode="numeric"
                            placeholder={y}
                            className={inputCls}
                          />
                        ))}
                      </div>
                    </Field>
                    <Field label="Número de empleados">
                      <input
                        data-testid="valuation-empleados"
                        value={fin.empleados}
                        onChange={setFinField("empleados")}
                        inputMode="numeric"
                        placeholder="Ej.: 12"
                        className={inputCls}
                      />
                    </Field>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="valuation-step1-continue"
                      className="flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-ink transition-all duration-300 hover:bg-gold-light disabled:opacity-60"
                    >
                      {loading ? "Calculando..." : "Continuar"}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                    <p className="text-xs leading-relaxed text-slate-500">{LEGAL_TXT}</p>
                  </motion.form>
                )}

                {step === 2 && (
                  <motion.form
                    key="s2"
                    initial={{ opacity: 0, x: -18 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 18 }}
                    transition={{ duration: 0.35 }}
                    onSubmit={submitStep2}
                    data-testid="valuation-step2-form"
                    className="space-y-7"
                  >
                    <Field label="% de facturación de tus 3 principales clientes">
                      <ChoiceGroup testid="valuation-conc" options={CONC} value={cual.concentracion_clientes} onChange={(v) => setCual({ ...cual, concentracion_clientes: v })} />
                    </Field>
                    <Field label="Dependencia del propietario en el día a día" hint="1 = la empresa funciona sin él · 5 = depende totalmente de él">
                      <ChoiceGroup
                        testid="valuation-dep"
                        options={[1, 2, 3, 4, 5].map((n) => ({ v: String(n), l: String(n) }))}
                        value={cual.dependencia_propietario}
                        onChange={(v) => setCual({ ...cual, dependencia_propietario: v })}
                      />
                    </Field>
                    <Field label="% de ingresos recurrentes o contractuales">
                      <ChoiceGroup testid="valuation-rec" options={REC} value={cual.ingresos_recurrentes} onChange={(v) => setCual({ ...cual, ingresos_recurrentes: v })} />
                    </Field>
                    <Field label="¿Existe un equipo directivo con capacidad de gestión independiente?">
                      <ChoiceGroup testid="valuation-equipo" options={SINO} value={cual.equipo_directivo} onChange={(v) => setCual({ ...cual, equipo_directivo: v })} />
                    </Field>
                    <Field label="Antigüedad de la empresa">
                      <ChoiceGroup testid="valuation-ant" options={ANT} value={cual.antiguedad_empresa} onChange={(v) => setCual({ ...cual, antiguedad_empresa: v })} />
                    </Field>
                    <Field label="¿Hay inmuebles en el balance de la sociedad?">
                      <ChoiceGroup testid="valuation-inmuebles" options={SINO} value={cual.inmuebles_balance} onChange={(v) => setCual({ ...cual, inmuebles_balance: v })} />
                    </Field>
                    <Field label="Inversión anual necesaria para mantener la actividad">
                      <ChoiceGroup testid="valuation-inv" options={INV} value={cual.inversion_mantenimiento} onChange={(v) => setCual({ ...cual, inversion_mantenimiento: v })} />
                    </Field>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setStep(1)}
                        data-testid="valuation-back-2"
                        className="flex items-center gap-2 border border-gold/40 px-6 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold/10"
                      >
                        <ArrowLeft className="h-4 w-4" /> Atrás
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        data-testid="valuation-step2-continue"
                        className="flex flex-1 items-center justify-center gap-3 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-ink transition-all duration-300 hover:bg-gold-light disabled:opacity-60"
                      >
                        {loading ? "Calculando..." : "Continuar"}
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-xs leading-relaxed text-slate-500">{LEGAL_TXT}</p>
                  </motion.form>
                )}

                {step === 3 && (
                  <motion.form
                    key="s3"
                    initial={{ opacity: 0, x: -18 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 18 }}
                    transition={{ duration: 0.35 }}
                    onSubmit={submitStep3}
                    data-testid="valuation-step3-form"
                    className="space-y-5"
                  >
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Nombre">
                        <input data-testid="valuation-nombre" value={lead.nombre} onChange={setLeadField("nombre")} required className={inputCls} />
                      </Field>
                      <Field label="Empresa">
                        <input data-testid="valuation-empresa" value={lead.empresa} onChange={setLeadField("empresa")} required className={inputCls} />
                      </Field>
                      <Field label="Cargo">
                        <input data-testid="valuation-cargo" value={lead.cargo} onChange={setLeadField("cargo")} required className={inputCls} />
                      </Field>
                      <Field label="Teléfono">
                        <input data-testid="valuation-telefono" type="tel" value={lead.telefono} onChange={setLeadField("telefono")} required className={inputCls} />
                      </Field>
                    </div>
                    <Field label="Email">
                      <input data-testid="valuation-email" type="email" value={lead.email} onChange={setLeadField("email")} required className={inputCls} />
                    </Field>
                    <Field label="Horizonte de venta">
                      <select
                        data-testid="valuation-horizonte"
                        value={lead.horizonte_venta}
                        onChange={setLeadField("horizonte_venta")}
                        required
                        className={`${inputCls} appearance-none bg-ink-2`}
                      >
                        <option value="" disabled className="bg-ink-2">Selecciona una opción</option>
                        {HORIZONTE.map((h) => (
                          <option key={h.v} value={h.v} className="bg-ink-2 text-white">{h.l}</option>
                        ))}
                      </select>
                    </Field>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Deuda financiera aproximada (€)" hint="Opcional — ajusta el valor del equity">
                        <input data-testid="valuation-deuda" value={lead.deuda} onChange={setLeadField("deuda")} inputMode="numeric" placeholder="0" className={inputCls} />
                      </Field>
                      <Field label="Tesorería aproximada (€)" hint="Opcional — ajusta el valor del equity">
                        <input data-testid="valuation-tesoreria" value={lead.tesoreria} onChange={setLeadField("tesoreria")} inputMode="numeric" placeholder="0" className={inputCls} />
                      </Field>
                    </div>
                    <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-slate-300" data-testid="valuation-consent-privacy">
                      <input
                        type="checkbox"
                        checked={lead.consent_privacidad}
                        onChange={(e) => setLead({ ...lead, consent_privacidad: e.target.checked })}
                        className="mt-1 h-4 w-4 shrink-0 accent-gold"
                        data-testid="valuation-check-privacy"
                      />
                      <span>
                        He leído y acepto la{" "}
                        <Link to="/privacidad" className="text-gold underline underline-offset-2" data-testid="valuation-privacy-link">
                          política de privacidad
                        </Link>
                        . *
                      </span>
                    </label>
                    <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-slate-300" data-testid="valuation-consent-commercial">
                      <input
                        type="checkbox"
                        checked={lead.consent_comercial}
                        onChange={(e) => setLead({ ...lead, consent_comercial: e.target.checked })}
                        className="mt-1 h-4 w-4 shrink-0 accent-gold"
                        data-testid="valuation-check-commercial"
                      />
                      <span>Acepto recibir comunicaciones comerciales de AJG M&amp;A Consulting.</span>
                    </label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setStep(2)}
                        data-testid="valuation-back-3"
                        className="flex items-center gap-2 border border-gold/40 px-6 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold/10"
                      >
                        <ArrowLeft className="h-4 w-4" /> Atrás
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        data-testid="valuation-step3-submit"
                        className="flex flex-1 items-center justify-center gap-3 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-ink transition-all duration-300 hover:bg-gold-light disabled:opacity-60"
                      >
                        {loading ? "Enviando..." : "Recibir mi informe de valoración"}
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-xs leading-relaxed text-slate-500">{LEGAL_TXT}</p>
                  </motion.form>
                )}
              </AnimatePresence>
            </Reveal>

            <Reveal delay={0.12} className="lg:sticky lg:top-32">
              <div data-testid="valuation-result-card" className="relative border border-gold/20 bg-ink-3/60 p-8 md:p-10">
                <div className="pointer-events-none absolute -left-2 -top-2 h-6 w-6 border-l border-t border-gold" />
                <div className="pointer-events-none absolute -bottom-2 -right-2 h-6 w-6 border-b border-r border-gold" />
                <AnimatePresence mode="wait">
                  <motion.div key={done ? "done" : `${step}-${!!pub}-${!!score}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                    {panel()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </Reveal>
          </div>
        )}
      </div>
    </section>
  );
}
